import type { UseQueryResult } from '@tanstack/react-query';
import { BusinessError } from '@/api/envelope';

/** O que o selo E-Malote de um modelo mostra. */
export type EstadoDaContagemEmalote =
  | { tipo: 'carregando' }
  | { tipo: 'sem-conexao' }
  | { tipo: 'sem-contagem'; motivo: string }
  | { tipo: 'erro'; motivo: string }
  | { tipo: 'pronta'; quantidade: number };

export type ConsultaDaContagemEmalote = Pick<
  UseQueryResult<number, Error>,
  'data' | 'error' | 'isError' | 'isFetching' | 'fetchStatus' | 'refetch'
>;

/*
  O erro do próprio navegador chega em inglês: o limite de tempo do client (`AbortSignal.timeout`)
  vira "signal timed out", e a falta de resposta do servidor, "Failed to fetch" (ou "NetworkError…",
  "Load failed", conforme o navegador). A dica fala português; o resto é a mensagem do servidor.
*/
const FALHA_DE_REDE = /failed to fetch|networkerror|load failed/i;

function motivoDaFalha(erro: Error | null): string {
  if (!erro) return '';
  if (erro.name === 'TimeoutError' || erro.name === 'AbortError') {
    return 'A contagem passou do tempo de espera.';
  }
  if (erro instanceof TypeError && FALHA_DE_REDE.test(erro.message)) {
    return 'O servidor não respondeu.';
  }
  return erro.message;
}

/**
 * A ordem existe para o selo nunca mostrar um número que a consulta não deu. Antes, erro,
 * recusa e falta de rede viravam "Total: 0" (LC-11).
 *
 * - Pausada é a consulta sem rede: o React Query não busca nem erra, e sem esta saída o selo
 *   ficaria carregando para sempre.
 * - Buscando vem antes do erro para o "tentar de novo" ter resposta na tela.
 * - Sem contagem é a resposta do servidor sem o número (`BusinessError`, de `fetchContar*Emalote`):
 *   a recusa em HTTP 200, como o contribuinte sem caixa de entrada ativa, ou o sucesso sem o
 *   número. Não é falha de carga: repetir dá a mesma resposta, e o selo não oferece "tentar de novo".
 * - Erro é a falha de carga: o HTTP, o limite de tempo, a falta de resposta.
 * - Sem número e sem erro, a consulta ainda não respondeu.
 */
export function estadoDaContagemEmalote(
  consulta: ConsultaDaContagemEmalote
): EstadoDaContagemEmalote {
  if (consulta.fetchStatus === 'paused') return { tipo: 'sem-conexao' };
  if (consulta.isFetching) return { tipo: 'carregando' };
  if (consulta.isError) {
    return consulta.error instanceof BusinessError
      ? { tipo: 'sem-contagem', motivo: consulta.error.message }
      : { tipo: 'erro', motivo: motivoDaFalha(consulta.error) };
  }
  if (typeof consulta.data === 'number') return { tipo: 'pronta', quantidade: consulta.data };
  return { tipo: 'carregando' };
}
